// MobileApplicationProgressSheet.jsx — mobile-only progress sheet that opens
// when an app card is tapped. Mirrors the Flutter apply_progress_sheet layout
// across its four stages: loading, optimising, review-resume, and the active
// filling/submitting flow. Desktop ApplicationProgressPanel is left untouched.
import { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import {
  ArrowLeft, ChevronDown, Download, StopCircle, RefreshCw, Check, Sparkles,
  AlertCircle, X as XIcon, HelpCircle, Mail, Lock,
} from "lucide-react"
import { doc, onSnapshot, setDoc } from "firebase/firestore"
import { db } from "../../firebaseConfig"
import { initials } from "../JobCard"
import {
  ResumeCanvas, APPLY_AGENT_API, DEFAULT_WORKDAY_PASSWORD,
} from "./ApplicationsTab"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'

// Flutter mobile palette
const INK          = "#0F172A"
const INK_MUTED    = "#64748B"
const INK_FAINT    = "#94A3B8"
const BG           = "#FFFFFF"
const SURFACE_ALT  = "#F3F2ED"
const BORDER_SOFT  = "#E6E5DF"
const LIVE_ACCENT  = "#E8702A"
const RED_ACCENT   = "#B91C1C"
const SUCCESS      = "#16A34A"

const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"
const KNOWN_STATUSES = new Set([
  "pending_optimization", "pending_resume_review", "processing", "completed",
  "failed", "job_expired", "pending_verification", "credentials_error",
  "additional_information_required", "skipped",
])

function parseDate(raw) {
  if (!raw) return new Date()
  if (typeof raw?.toDate === "function") return raw.toDate()
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? new Date() : d
}
function parseStatus(status = "") {
  return KNOWN_STATUSES.has(status) ? status : "queued"
}
function normalize(snap) {
  const data = snap.data() || {}
  return {
    ...data,
    id: snap.id,
    jobTitle: data.jobTitle || "",
    company: data.company || "",
    logoUrl: data.logoUrl || "",
    jobUrl: data.jobUrl || "",
    description: data.description || "",
    appliedAt: parseDate(data.appliedAt),
    status: parseStatus(data.status),
  }
}
function formatDateTime(dt) {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
  const h = dt.getHours() % 12 || 12
  const ampm = dt.getHours() < 12 ? "AM" : "PM"
  const mm = String(dt.getMinutes()).padStart(2, "0")
  return `${months[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()} at ${h}:${mm} ${ampm}`
}

// ── Company logo ───────────────────────────────────────────────────────────
function CompanyLogo({ company, logoUrl, size = 40 }) {
  const [bfErr, setBfErr] = useState(false)
  const [fbErr, setFbErr] = useState(false)
  const domain = company
    ? company.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null
  const src = !bfErr ? bfUrl : (!fbErr && logoUrl ? logoUrl : null)
  const onErr = () => { if (!bfErr) setBfErr(true); else setFbErr(true) }

  const base = { width: size, height: size, borderRadius: 10, flexShrink: 0 }
  if (src) {
    return (
      <span style={{
        ...base, background: "#fff", border: `1px solid ${BORDER_SOFT}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <img src={src} alt="" onError={onErr}
          style={{ width: "80%", height: "80%", objectFit: "contain" }} />
      </span>
    )
  }
  return (
    <span style={{
      ...base, background: SURFACE_ALT,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      <span style={{ fontSize: size * 0.3, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>
        {initials(company)}
      </span>
    </span>
  )
}

// ── Pulsing dot ────────────────────────────────────────────────────────────
function PulsingDot({ color = LIVE_ACCENT }) {
  return (
    <span style={{ position: "relative", display: "inline-block", width: 8, height: 8 }}>
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%", background: color,
      }} />
      <span style={{
        position: "absolute", inset: 0, borderRadius: "50%", background: color,
        animation: "mprog-pulse 1.6s ease-out infinite",
      }} />
    </span>
  )
}

// ── Section label ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: INK_FAINT,
      letterSpacing: "0.08em", textTransform: "uppercase",
      marginBottom: 8,
    }}>{children}</div>
  )
}

// ── Progress-overview card (used by stage 1 & 2 as the top summary) ───────
function ProgressOverview({ stage, title, status, color = LIVE_ACCENT, pulse }) {
  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {pulse ? <PulsingDot color={color} /> : <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />}
        <div style={{ fontSize: 13, fontWeight: 700, color }}>{status}</div>
      </div>
      <div style={{ marginTop: 10, fontSize: 20, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>
        {title}
      </div>
      <div style={{
        marginTop: 14, display: "flex", gap: 6,
      }}>
        {[1, 2, 3, 4, 5].map(n => (
          <span key={n} style={{
            flex: 1, height: 4, borderRadius: 999,
            background: n <= stage ? color : SURFACE_ALT,
          }} />
        ))}
      </div>
    </div>
  )
}

// ── Skeleton loader ────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
      {[112, 60, 72].map((h, i) => (
        <div key={i} style={{
          height: h, borderRadius: 16, background: SURFACE_ALT,
          animation: "mprog-skeleton 1.4s ease-in-out infinite",
        }} />
      ))}
    </div>
  )
}

// ── Stage 1 status (optimising card) ───────────────────────────────────────
function OptimisingCard({ currentAction, elapsed }) {
  const remaining = Math.max(0, 30 - elapsed)
  const frac = Math.max(0, remaining / 30)
  const R = 16, C = 2 * Math.PI * R
  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <PulsingDot color={LIVE_ACCENT} />
        <div style={{ fontSize: 13, fontWeight: 700, color: LIVE_ACCENT }}>Optimising</div>
      </div>
      <p style={{ margin: "8px 0 12px", fontSize: 13, color: INK_MUTED, lineHeight: 1.5 }}>
        {currentAction || "Tailoring your resume to the job description."}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          flex: 1, fontSize: 11.5, color: `${LIVE_ACCENT}A6`, fontStyle: "italic",
        }}>
          {elapsed >= 30 ? "Almost done…" : "It may take up to ~30 seconds"}
        </div>
        <svg width={40} height={40} viewBox="0 0 40 40">
          <circle cx={20} cy={20} r={R} fill="none" stroke={`${LIVE_ACCENT}26`} strokeWidth={2.5} />
          <circle
            cx={20} cy={20} r={R} fill="none"
            stroke={LIVE_ACCENT} strokeWidth={2.5}
            strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
            transform="rotate(-90 20 20)"
            strokeLinecap="round"
          />
          <text x={20} y={20} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill={LIVE_ACCENT} fontFamily={FF}>
            {remaining}
          </text>
        </svg>
      </div>
    </div>
  )
}

function DetailsTable({ jobUrl, ats, createdAt }) {
  const rows = []
  if (jobUrl) rows.push(["URL", jobUrl])
  if (ats)    rows.push(["ATS", ats])
  if (createdAt) rows.push(["Created", formatDateTime(createdAt)])
  if (rows.length === 0) return null
  return (
    <div style={{
      padding: "14px 16px 16px",
      borderTop: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: INK_FAINT, letterSpacing: 0.8 }}>DETAILS</div>
      <div style={{ height: 1, background: BORDER_SOFT, margin: "10px 0" }} />
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{ width: 56, fontSize: 12, color: INK_FAINT, fontWeight: 500, flexShrink: 0 }}>{k}</div>
          <div style={{
            flex: 1, fontSize: 12, color: INK_MUTED,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{v}</div>
        </div>
      ))}
    </div>
  )
}

// ── Stage 2 match card ─────────────────────────────────────────────────────
function MatchCard({ percentage, matched, total, keywords }) {
  const hard = keywords.filter(k => (k?.type || "hard") === "hard")
  const hardMatched  = hard.filter(k => k.matched)
  const hardMissing  = hard.filter(k => !k.matched)

  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{
          fontSize: 38, fontWeight: 800, color: INK, letterSpacing: "-1px", lineHeight: 1,
        }}>{percentage || 0}%</div>
        <div style={{ paddingBottom: 6, fontSize: 12, color: INK_MUTED }}>
          {matched || 0} of {total || 0} keywords matched
        </div>
      </div>
      <div style={{ marginTop: 12, height: 6, borderRadius: 999, background: SURFACE_ALT, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${Math.min(100, Math.max(0, percentage || 0))}%`,
          background: LIVE_ACCENT, borderRadius: 999,
        }} />
      </div>
      {hardMissing.length > 0 && (
        <>
          <div style={{ marginTop: 16, fontSize: 11, fontWeight: 700, color: INK_FAINT, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Missing hard skills
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {hardMissing.map((k, i) => (
              <span key={i} style={{
                padding: "4px 10px", borderRadius: 999,
                background: "#FEF3C7", color: "#854D0E",
                fontSize: 11, fontWeight: 600,
              }}>{k.keyword}</span>
            ))}
          </div>
        </>
      )}
      {hardMatched.length > 0 && (
        <>
          <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, color: INK_FAINT, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Covered
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {hardMatched.map((k, i) => (
              <span key={i} style={{
                padding: "4px 10px", borderRadius: 999,
                background: SURFACE_ALT, color: INK_MUTED,
                fontSize: 11, fontWeight: 600,
              }}>✓ {k.keyword}</span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Changes list (stage 2) ────────────────────────────────────────────────
const MOBILE_CHANGE_TYPE_LABEL = {
  rewrite: "Rewrite",
  insert:  "Added",
  tighten: "Trimmed",
  remove:  "Removed",
}
const MOBILE_CHANGE_TYPE_DOT = {
  rewrite: INK,
  insert:  LIVE_ACCENT,
  tighten: INK_FAINT,
  remove:  RED_ACCENT,
}

function MobileChangeRow({ change, isLast }) {
  const [open, setOpen] = useState(false)
  const typeLabel = MOBILE_CHANGE_TYPE_LABEL[change?.type] || "Edit"
  const dotColor  = MOBILE_CHANGE_TYPE_DOT[change?.type] || INK
  const before = (change?.before || "").trim()
  const after  = (change?.after  || "").trim()
  const showBefore = change?.type === "remove" || (before && change?.type !== "insert")
  const showAfter  = after && change?.type !== "remove"
  const hasDetail  = showBefore || showAfter
  const keywords   = Array.isArray(change?.keywords) ? change.keywords.filter(Boolean) : []
  const headline   = change?.reason || change?.location || "Edit"
  const subline    = change?.reason ? (change?.location || change?.section || "") : (change?.section || "")

  return (
    <div style={{
      paddingBottom: isLast ? 0 : 12,
      borderBottom: isLast ? "none" : `1px solid ${BORDER_SOFT}`,
    }}>
      <button
        type="button"
        onClick={() => hasDetail && setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "flex-start", gap: 10,
          background: "transparent", border: 0, padding: 0, textAlign: "left",
          cursor: hasDetail ? "pointer" : "default",
        }}
      >
        <span style={{
          flexShrink: 0, marginTop: 7,
          width: 6, height: 6, borderRadius: 999, background: dotColor,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 650, color: INK,
            lineHeight: 1.5, letterSpacing: "-0.1px",
          }}>
            {headline}
          </div>
          <div style={{
            marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            fontSize: 11, color: INK_FAINT,
          }}>
            {subline && <span>{subline}</span>}
            {subline && <span style={{ opacity: 0.6 }}>·</span>}
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {typeLabel}
            </span>
            {keywords.map((kw, i) => (
              <span key={i} style={{
                padding: "1px 7px", borderRadius: 999,
                background: SURFACE_ALT, color: INK_MUTED,
                fontSize: 10, fontWeight: 650,
              }}>+ {kw}</span>
            ))}
          </div>
        </div>
        {hasDetail && (
          <ChevronDown
            size={14}
            style={{
              flexShrink: 0, marginTop: 4, color: INK_FAINT,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform .15s",
            }}
          />
        )}
      </button>

      {open && hasDetail && (
        <div style={{
          marginTop: 10, marginLeft: 16,
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          {showBefore && before && (
            <div style={{
              padding: "8px 10px", borderRadius: 10,
              background: BG, border: `1px solid ${BORDER_SOFT}`,
              fontSize: 12, lineHeight: 1.55, color: INK_FAINT,
              textDecoration: "line-through", whiteSpace: "pre-wrap",
            }}>{before}</div>
          )}
          {showAfter && (
            <div style={{
              padding: "8px 10px", borderRadius: 10,
              background: SURFACE_ALT,
              fontSize: 12, lineHeight: 1.55, color: INK,
              whiteSpace: "pre-wrap",
            }}>{after}</div>
          )}
        </div>
      )}
    </div>
  )
}

function MobileChangesCard({ changes }) {
  return (
    <div style={{
      padding: 14, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      {changes.map((c, i) => (
        <MobileChangeRow key={i} change={c} isLast={i === changes.length - 1} />
      ))}
    </div>
  )
}

// ── Agent status (stage 3+) ───────────────────────────────────────────────
function AgentStatusCard({ dotColor, label, text, isDone, isError, waitingReply, pulsing }) {
  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {pulsing && !isDone && !isError && !waitingReply
          ? <PulsingDot color={dotColor} />
          : <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor }} />}
        <div style={{ fontSize: 13, fontWeight: 700, color: dotColor }}>{label}</div>
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 13, color: INK_MUTED, lineHeight: 1.55 }}>{text}</p>
    </div>
  )
}

function QuestionCard({ question, onTap }) {
  return (
    <button
      onClick={onTap}
      style={{
        width: "100%", textAlign: "left",
        padding: 16, borderRadius: 16,
        background: "#FFFFFF",
        border: `1px solid rgba(232,112,42,0.24)`,
        color: INK, cursor: "pointer",
        fontSize: 14, lineHeight: 1.5, fontFamily: FF,
      }}
    >
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: LIVE_ACCENT, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 8 }}>
        <HelpCircle size={13} /> Needs your input
      </div>
      {question}
    </button>
  )
}

function SubmitCard({ onConfirm, submitting }) {
  return (
    <div style={{
      padding: 16, borderRadius: 16,
      background: "#FFFFFF", border: `1px solid ${BORDER_SOFT}`,
    }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: INK }}>Ready to submit</h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: INK_MUTED, lineHeight: 1.5 }}>
        The agent has filled your application. Confirm to submit.
      </p>
      <button
        onClick={onConfirm}
        disabled={submitting}
        style={{
          width: "100%", height: 48,
          borderRadius: 12, border: "none",
          background: INK, color: "#fff",
          fontFamily: FF, fontSize: 14.5, fontWeight: 800,
          cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.75 : 1,
        }}
      >
        {submitting ? "Submitting…" : "Submit application"}
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function MobileApplicationProgressSheet({
  application, userId, resumeId, user, onClose,
}) {
  const [docData, setDocData]           = useState(application)
  const [loading, setLoading]           = useState(true)
  const [stage, setStage]               = useState(1)
  const [currentAction, setCurrentAction] = useState("Connecting…")
  const [pendingQuestion, setPendingQuestion] = useState("")
  const [waitingReply, setWaitingReply] = useState(false)
  const [isReview, setIsReview]         = useState(false)
  const [isDone, setIsDone]             = useState(false)
  const [isError, setIsError]           = useState(false)
  const [elapsed, setElapsed]           = useState(0)
  const [sessionStarting, setSessionStarting] = useState(false)
  const [submitting, setSubmitting]     = useState(false)

  const appId = application?.id
  const optimizationData = docData?.optimizationData || null
  const resumeUrl        = docData?.resumeUrl || ""
  const optimisedResumeId = docData?.optimisedResumeId || ""
  const jobUrl           = docData?.jobUrl || application?.jobUrl || ""
  const sessionId        = docData?.sessionId || application?.sessionId || ""
  const createdAt        = docData?.appliedAt || application?.appliedAt
  const ats              = docData?.ats || docData?.atsProvider || ""

  // ── Firestore subscription — mirrors desktop ApplicationProgressPanel ────
  useEffect(() => {
    if (!userId || !appId) return
    setLoading(true)
    const unsub = onSnapshot(
      doc(db, "users", userId, "applications", appId),
      snap => {
        const next = snap.exists() ? normalize(snap) : application
        setDocData(next)
        const preSession = next.status === "pending_optimization" || next.status === "pending_resume_review"
        if (preSession) {
          if (next.optimizationData && next.resumeUrl) {
            setStage(2); setCurrentAction("Resume optimised — review below")
          } else {
            setStage(1); setCurrentAction("Tailoring your resume to the job description.")
          }
        } else if (next.status === "completed") {
          setStage(5); setIsDone(true); setCurrentAction("Application submitted!")
        } else if (next.status === "failed") {
          setIsError(true); setCurrentAction("Application paused")
        } else {
          setStage(s => Math.max(s, 3)); setCurrentAction("Connecting…")
        }
        setLoading(false)
      },
      err => {
        console.error("Failed to load application progress:", err)
        setIsError(true); setLoading(false)
      }
    )
    return () => unsub()
  }, [userId, appId])

  // Optimise elapsed timer
  useEffect(() => {
    if (stage !== 1) return undefined
    const t = setInterval(() => setElapsed(v => v + 1), 1000)
    return () => clearInterval(t)
  }, [stage])

  // SSE for active session
  useEffect(() => {
    if (!sessionId || stage < 3 || isDone) return undefined
    const es = new EventSource(`${APPLY_AGENT_API}/apply/events/${sessionId}`)
    es.onmessage = (evt) => {
      if (!evt.data || evt.data === "[DONE]") return
      try {
        const ev = JSON.parse(evt.data)
        handleEvent(ev)
        if (["done", "cancelled"].includes(ev.type)) es.close()
      } catch {}
    }
    es.onerror = () => setCurrentAction("Reconnecting…")
    return () => es.close()
  }, [sessionId, stage, isDone])

  const handleEvent = (ev) => {
    const type = ev.type || ""
    const text = ev.text || ev.message || ""
    const toolLabels = {
      open_application: "Opening application",
      get_page_state: "Reading page",
      click_popup_option: "Selecting option",
      sign_in: "Signing in",
      create_account: "Creating account",
      fill_field: "Filling field",
      select_option: "Selecting option",
      check_checkbox: "Checking box",
      upload_resume: "Uploading resume",
      submit_step: "Submitting section",
      check_email_verified: "Verifying email",
      go_back: "Going back",
      close_browser: "Closing browser",
    }
    if (type === "queued")        { setCurrentAction(`Position #${ev.position || 1} in queue…`); setStage(s => Math.max(s, 3)) }
    else if (type === "processing") { setCurrentAction("Starting application…"); setStage(s => Math.max(s, 3)) }
    else if (type === "tab")        { if (text) setCurrentAction(text); setStage(s => Math.max(s, text.toLowerCase().includes("submit") ? 4 : 3)) }
    else if (type === "status" || type === "message") { if (text) setCurrentAction(text) }
    else if (type === "question")   { setPendingQuestion(text); setWaitingReply(true) }
    else if (type === "tool_start") { const tool = ev.tool || ""; setCurrentAction(toolLabels[tool] || tool.replace(/_/g, " ")); if (tool === "submit_step") setStage(s => Math.max(s, 4)) }
    else if (type === "review")     { setStage(4); setIsReview(true); setWaitingReply(false); setCurrentAction("Ready to submit") }
    else if (type === "done")       { setStage(5); setIsDone(true); setIsReview(false); setWaitingReply(false); setCurrentAction("Application submitted!") }
    else if (type === "cancelled")  { setIsError(true); setCurrentAction("Session cancelled") }
  }

  // ── Actions ─────────────────────────────────────────────────────────────
  const startSession = async (useOptimised) => {
    if (!jobUrl || !userId || !appId) return
    setSessionStarting(true); setIsError(false)
    try {
      const chosenResumeId = useOptimised && optimisedResumeId ? optimisedResumeId : resumeId
      const res = await fetch(`${APPLY_AGENT_API}/apply/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_url: jobUrl, email: user?.email || "",
          password: DEFAULT_WORKDAY_PASSWORD,
          user_id: userId, resume_id: chosenResumeId || "",
          application_id: appId,
        }),
      })
      if (!res.ok) throw new Error(`Failed to start session (${res.status})`)
      if (useOptimised) {
        setDoc(doc(db, "users", userId, "applications", appId),
          { useOptimizedResume: true }, { merge: true }).catch(() => {})
      }
      setStage(3)
      setCurrentAction("Agent will apply in the background")
      toast.success("Agent will apply in the background")
    } catch (err) {
      console.error("Failed to start application session:", err)
      setIsError(true); setCurrentAction("Failed to start application")
      toast.error("Failed to start application")
    } finally {
      setSessionStarting(false)
    }
  }

  const confirmSubmit = async () => {
    if (!sessionId || submitting) return
    setSubmitting(true)
    try {
      setIsReview(false); setCurrentAction("Submitting…")
      await fetch(`${APPLY_AGENT_API}/apply/reply/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: [{ label: "__confirm__", value: "submit" }] }),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const cancelSession = async () => {
    if (!window.confirm("Stop this application?")) return
    if (sessionId) {
      await fetch(`${APPLY_AGENT_API}/apply/cancel/${sessionId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
      }).catch(() => {})
    }
    onClose?.()
  }

  // ── Derived meta for stage 2 ────────────────────────────────────────────
  const meta = optimizationData?.optimizationMetadata || {}
  const keywords = Array.isArray(meta.keywords) ? meta.keywords : []
  const totalKeywords = meta.total || keywords.length
  const matchedCount = meta.matched || keywords.filter(k => k?.matched).length
  const changes = Array.isArray(meta.changes) ? meta.changes : []

  const dotColor = isDone ? SUCCESS : isError ? RED_ACCENT : LIVE_ACCENT
  const activeStage = stage >= 3

  // ── Shared header ───────────────────────────────────────────────────────
  const Header = (
    <div style={{
      padding: `calc(12px + env(safe-area-inset-top)) 16px 12px`,
      display: "flex", alignItems: "center", gap: 10,
      background: BG,
      boxShadow: "0 3px 12px rgba(15,23,42,0.07)",
      position: "relative", zIndex: 1,
    }}>
      <button
        onClick={onClose}
        aria-label="Back"
        style={{
          width: 32, height: 32, borderRadius: 999,
          background: SURFACE_ALT, border: "none", color: INK_MUTED,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", flexShrink: 0,
        }}
      >
        <ArrowLeft size={18} />
      </button>
      <CompanyLogo company={docData?.company || application?.company} logoUrl={docData?.logoUrl} size={40} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: INK, letterSpacing: "-0.2px",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{docData?.jobTitle || application?.jobTitle || "Role"}</div>
        <div style={{
          fontSize: 12, color: INK_MUTED, marginTop: 2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{docData?.company || application?.company || "Company"}</div>
      </div>
      {stage === 2 && resumeUrl && (
        <a
          href={resumeUrl} target="_blank" rel="noreferrer"
          aria-label="Download resume"
          style={{
            width: 34, height: 34, borderRadius: 9,
            background: SURFACE_ALT, color: INK_MUTED,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Download size={17} />
        </a>
      )}
      {activeStage && !isDone && !isError && (
        <button
          onClick={cancelSession}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "6px 10px", borderRadius: 8,
            background: "rgba(185,28,28,0.08)",
            border: `1px solid rgba(185,28,28,0.2)`,
            color: RED_ACCENT, fontFamily: FF,
            fontSize: 11, fontWeight: 600, cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <StopCircle size={12} /> Stop
        </button>
      )}
    </div>
  )

  // ── Body per stage ──────────────────────────────────────────────────────
  let body
  if (loading) {
    body = <Skeleton />
  } else if (isDone) {
    body = (
      <div style={{ padding: "32px 20px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "#DCFCE7", color: SUCCESS,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          <Check size={34} strokeWidth={2.5} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>Application submitted</div>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: INK_MUTED, lineHeight: 1.5, maxWidth: 320 }}>
          Your agent handed this off. Keep an eye on your inbox for a recruiter reply.
        </p>
      </div>
    )
  } else if (isError) {
    body = (
      <div style={{ padding: "32px 20px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "#FEE2E2", color: RED_ACCENT,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          marginBottom: 16,
        }}>
          <AlertCircle size={34} strokeWidth={2.3} />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: INK, letterSpacing: "-0.3px" }}>Application paused</div>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: INK_MUTED, lineHeight: 1.5, maxWidth: 320 }}>
          {currentAction || "The agent couldn't finish this one. Try again when you're ready."}
        </p>
        <button
          onClick={() => startSession(true)}
          disabled={sessionStarting}
          style={{
            marginTop: 20,
            padding: "11px 18px", borderRadius: 999,
            background: LIVE_ACCENT, color: "#fff", border: "none",
            fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <RefreshCw size={14} />
          {sessionStarting ? "Restarting…" : "Try again"}
        </button>
      </div>
    )
  } else if (stage === 1) {
    body = (
      <div style={{ padding: "18px 16px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
        <ProgressOverview stage={1} title="Optimising your resume" status="In progress" pulse />
        <div>
          <SectionLabel>Current activity</SectionLabel>
          <OptimisingCard currentAction={currentAction} elapsed={elapsed} />
          <DetailsTable jobUrl={jobUrl} ats={ats} createdAt={createdAt} />
        </div>
      </div>
    )
  } else if (stage === 2) {
    body = (
      <div style={{ padding: "18px 16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
        <ProgressOverview stage={2} title="Review your resume" status="Needs you" color={LIVE_ACCENT} />
        <div>
          <SectionLabel>Resume match</SectionLabel>
          <MatchCard percentage={meta.percentage || 0} matched={matchedCount} total={totalKeywords} keywords={keywords} />
        </div>
        {changes.length > 0 && (
          <div>
            <SectionLabel>What changed</SectionLabel>
            <MobileChangesCard changes={changes} />
          </div>
        )}
        <div>
          <SectionLabel>Preview</SectionLabel>
          <div style={{
            borderRadius: 16, overflow: "hidden",
            background: "#fff", border: `1px solid ${BORDER_SOFT}`,
            padding: 8,
          }}>
            {resumeUrl ? (
              <ResumeCanvas url={resumeUrl} />
            ) : (
              <div style={{ padding: 40, textAlign: "center", color: INK_MUTED, fontSize: 13 }}>
                Resume unavailable
              </div>
            )}
          </div>
        </div>
      </div>
    )
  } else {
    const statusLabel = waitingReply ? "Needs your input" : "Agent working"
    body = (
      <div style={{ padding: "18px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
        <ProgressOverview
          stage={stage}
          title={isReview ? "Ready to submit" : stage === 4 ? "Submitting application" : "Filling your application"}
          status={isReview ? "Review" : "Live"}
          color={LIVE_ACCENT}
          pulse={!isReview && !waitingReply}
        />
        <AgentStatusCard
          dotColor={dotColor}
          label={statusLabel}
          text={currentAction || "Working on it…"}
          isDone={isDone}
          isError={isError}
          waitingReply={waitingReply}
          pulsing
        />
        {waitingReply && pendingQuestion && (
          <QuestionCard question={pendingQuestion} onTap={() => toast("Open the desktop view to answer this question.")} />
        )}
        {isReview && <SubmitCard onConfirm={confirmSubmit} submitting={submitting} />}
      </div>
    )
  }

  const showStage2Buttons = !loading && stage === 2

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 180,
      background: BG, color: INK, fontFamily: FF,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes mprog-pulse {
          0%   { transform: scale(0.9); opacity: 0.75; }
          70%  { transform: scale(2.4);  opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes mprog-skeleton {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>

      {Header}

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {body}
      </div>

      {showStage2Buttons && (
        <div style={{
          flexShrink: 0,
          padding: `10px 16px calc(10px + env(safe-area-inset-bottom))`,
          background: BG,
          borderTop: `1px solid ${BORDER_SOFT}`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <button
            onClick={() => startSession(false)}
            disabled={sessionStarting}
            style={{
              padding: "10px 4px", background: "transparent", border: "none",
              color: INK_MUTED, fontFamily: FF, fontSize: 12.5, fontWeight: 650,
              textDecoration: "underline", textUnderlineOffset: 3,
              cursor: sessionStarting ? "default" : "pointer",
            }}
          >
            Use original
          </button>
          <button
            onClick={() => startSession(true)}
            disabled={sessionStarting}
            style={{
              marginLeft: "auto",
              padding: "12px 22px", borderRadius: 999,
              background: INK, color: "#fff", border: "none",
              fontFamily: FF, fontSize: 13.5, fontWeight: 750,
              cursor: sessionStarting ? "default" : "pointer",
              boxShadow: "0 2px 8px rgba(15,23,42,0.18)",
            }}
          >
            {sessionStarting ? "Starting…" : "Approve & submit"}
          </button>
        </div>
      )}
    </div>
  )
}
